import React, { useEffect, useMemo, useState } from 'react';
import { Image, OverlayTrigger, Popover } from 'react-bootstrap';
import { useFormContext } from 'react-hook-form';
import { connect, useDispatch } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import {
  BackAndMultipleButtons,
  SaveButton,
  SaveTempButton,
} from 'components/base/BottomStepComponent';
import ContentSections from 'components/base/ContentSections';
import ListButtonsGroup from 'components/base/ListButtonsGroup';
import BasePrompt from 'components/base/prompt/BasePrompt';
import RejectPrompt from 'components/campaign/campaignCreation/RejectPrompt';
import MembershipInfoCard from 'components/customer/MembershipInfoCard';
import { MessageCreditCardContent } from 'components/message/MessageCredit';
import MessageOverviewTableSection from 'components/message/MessageOverviewTableSection';
import TestAccountPrompt from 'components/message/TestAccountPrompt';
import {
  DISPLAY_MESSAGE_TARGET_TYPE,
  MESSAGE_TARGET_TYPE,
  MessageChannel,
  TARGET_CUSTOMER_TYPE,
} from 'config/CustomEnums';
import {
  ActionType,
  ApprovalStatus,
  MessageStepConfig,
} from 'config/CustomEnums';
import { PermissionCodes } from 'config/PermissionCodes';
import { validate } from 'containers/engagement/message/CreateMessageValidate';

import { createAction, formatNumberWithCommas } from 'utils';
import { TimeFormater, formatDate } from 'utils/TimeFormatUtil';

import closeIcon from 'assets/images/closeWhite.svg';

import './CreateMessageStepFour.scss';

const mapPropsToState = (state) => ({
  languages: state.language.allList,
  isSuperuser: state.users.isSuperuser,
  userPermissions: state.users.userPermissions,
  selectedChannels: state.createMessage.selectedChannels,
  estimateCredit: state.createMessage.estimateCredit || 0,
  realCreditTotalSpent: state.messageList.realCreditTotalSpent,
  whatsappCreditTotalSpent: state.messageList.whatsappCreditTotalSpent,
  smsCreditTotalSpent: state.messageList.smsCreditTotalSpent,
  emailCreditTotalSpent: state.messageList.emailCreditTotalSpent,
  estimateSmsCredit: state.createMessage.estimateSmsCredit || 0,
  estimateWhatsappCredit: state.createMessage.estimateWhatsappCredit || 0,
  estimateEmailCredit: state.createMessage.estimateEmailCredit || 0,
  hideTestChannelTip:
    state.settings.adminTipConfig?.hideTestChannelTip || false,
});

function CreateMessageStepFour({
  languages,
  isSuperuser,
  userPermissions,
  selectedChannels,
  realCreditTotalSpent,
  estimateCredit,
  setShowSentPrompt,
  setIsTestSend,
  setActionType,
  whatsappCreditTotalSpent,
  smsCreditTotalSpent,
  emailCreditTotalSpent,
  estimateSmsCredit,
  estimateWhatsappCredit,
  estimateEmailCredit,
  hideTestChannelTip,
}) {
  const params = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const location = useLocation();

  const { getValues, setError, clearErrors } = useFormContext();

  const message = getValues();
  const hideTarget = message?.sendToAll || message?.recipientsFollowCampaign;
  message.displayChannels = message.channels?.toString();
  message.relatedCampaignName = message.relatedCampaign?.pk
    ? `[ID: ${message.relatedCampaign?.pk}] ${message.relatedCampaign?.name}`
    : '-';
  message.displayTargetType =
    DISPLAY_MESSAGE_TARGET_TYPE?.[message.targetType] || '-';
  const displayTargetLevels = (message.targetedLevels || [])
    .map((item) => item.levelName)
    .toString();
  const displayTargetedStaticSegments = (message.targetedStaticSegments || [])
    .map((item) => item.name)
    .toString();
  const displayTargetedDynamicSegments = (message.targetedDynamicSegments || [])
    .map((item) => item.name)
    .toString();
  message.displayTargetedDynamicSegments = '-';
  message.displayTargetedStaticSegments = '-';
  message.displayTargetLevels = '-';

  if (hideTarget) {
    message.displayTargetType = '-';
  } else if (message.targetType === MESSAGE_TARGET_TYPE.LEVEL) {
    message.displayTargetLevels = displayTargetLevels;
  } else if (message.targetType === MESSAGE_TARGET_TYPE.STATIC_SEGMENT) {
    message.displayTargetedStaticSegments = displayTargetedStaticSegments;
  } else if (message.targetType === MESSAGE_TARGET_TYPE.DYNAMIC_SEGMENT) {
    message.displayTargetedDynamicSegments = displayTargetedDynamicSegments;
  }
  message.deliveryDate = formatDate(
    message.scheduledDate,
    TimeFormater.dayMonthYearWeekTimeA,
  );
  message.displaySendToAll = message?.sendToAll ? 'Yes' : 'No';
  message.displayRecipientsFollowCampaign = message?.recipientsFollowCampaign
    ? 'Yes'
    : 'No';
  message.displayIsStartWithTemplate = message?.isStartWithTemplate
    ? 'Yes'
    : 'No';
  if (!message?.displayTemplateName) {
    message.displayTemplateName =
      message?.messageTemplate?.label || message?.messageTemplate?.value?.name;
  }

  if (location.pathname.includes('edit')) {
    message.relatedCampaignName = message.relatedCampaign?.pk
      ? `[ID: ${message.relatedCampaign?.pk}] ${message.relatedCampaign?.name}`
      : '-';
  }

  const [showRejectPrompt, setShowRejectPrompt] = useState(false);
  const [tempHideTestPopover, setTempHideTestPopover] = useState(false);
  const [showTestPrompt, setShowTestPrompt] = useState(false);
  const [showTimeoutPrompt, setShowTimeoutPrompt] = useState(false);
  const [approveConfirmAction, setApproveConfirmAction] = useState(null);
  const [showApprovePrompt, setShowApprovePrompt] = useState(false);

  useEffect(() => {
    dispatch({
      type: 'settings/getAdminTipConfig',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hideTestChannelTipByUpdateConfig = () => {
    dispatch({
      type: 'settings/updateAdminTipConfig',
      payload: {
        data: {
          hideTestChannelTip: true,
        },
      },
    });
  };

  const approvalStatus = getValues('approvalStatus');
  let tempActionType = null;
  const hasPermission = (requires) => {
    if (isSuperuser) return true;
    if (userPermissions.includes(requires) || !requires) return true;

    return false;
  };
  const hasSelectedTargetCustomer = (message) => {
    if (message.targetType === MESSAGE_TARGET_TYPE.LEVEL) {
      return message?.targetedLevels?.length > 0;
    }
    if (message.targetType === MESSAGE_TARGET_TYPE.STATIC_SEGMENT) {
      return message?.targetedStaticSegments?.length > 0;
    }
    if (message.targetType === MESSAGE_TARGET_TYPE.DYNAMIC_SEGMENT) {
      return message?.targetedDynamicSegments?.length > 0;
    }
    return false;
  };

  const stepChangeAction = (isBack) => {
    let previewStep = 2;
    if (MessageStepConfig[1].show(selectedChannels)) {
      previewStep = previewStep + 1;
    }
    if (MessageStepConfig[2].show(selectedChannels)) {
      previewStep = previewStep + 1;
    }
    if (MessageStepConfig[3].show(selectedChannels)) {
      previewStep = previewStep + 1;
    }
    if (MessageStepConfig[4].show(selectedChannels)) {
      previewStep = previewStep + 1;
    }
    const isValid = validate({
      getValues: getValues,
      setError,
      clearErrors,
      step: 5,
      isBack,
    });
    dispatch({
      type: 'createMessage/stepChange',
      payload: {
        isBack: isBack,
        step: previewStep,
        isValid,
      },
    });
  };

  const renderTabForLanguage = (language) => {
    return [
      <MessageOverviewTableSection message={message} language={language} />,
      <MembershipInfoCard
        title={'PROPERTIES'}
        data={message}
        fields={[
          [
            {
              title: 'Start with a template',
              field: 'displayIsStartWithTemplate',
            },
          ],
          [{ title: 'Template', field: 'displayTemplateName' }],
          [{ title: 'Name', field: 'name' }],
          [{ title: 'Message channel', field: 'displayChannels' }],
          [{ title: 'Related campaign', field: 'relatedCampaignName' }],
          [
            {
              title: 'Is this a direct marketing message?',
              field: 'isDirectMarketing',
              isBoolean: true,
            },
          ],
          [
            {
              title: 'Send to every customer (including non-member)',
              field: 'displaySendToAll',
            },
          ],
          [
            {
              title: 'Message recipients follow campaign',
              field: 'displayRecipientsFollowCampaign',
            },
          ],
          [{ title: 'Target customer', field: 'displayTargetType' }],
          [{ title: 'Level', field: 'displayTargetLevels' }],
          [{ title: 'Static segment', field: 'displayTargetedStaticSegments' }],
          [
            {
              title: 'Dynamic segment',
              field: 'displayTargetedDynamicSegments',
            },
          ],
          [{ title: 'Delivery Time', field: 'deliveryDate' }],
        ]}
      />,
    ];
  };

  const approvalButtonAction = () => {
    const values = getValues();
    console.log('values', saveActionType, values);
    if (saveActionType === ActionType.unpublish) {
      saveButtonAction(false, saveActionType);
      return;
    }

    if (new Date(message.scheduledDate) < new Date()) {
      setShowTimeoutPrompt(true);
      return;
    }

    if (values?.relatedCampaign?.isExclusive && !values?.sendToAll) {
      setApproveConfirmAction(saveActionType);
      setShowApprovePrompt(true);
      return;
    }

    if (hasSelectedTargetCustomer(values) && !values?.sendToAll) {
      saveButtonAction(false, saveActionType);
      return;
    }
    setApproveConfirmAction(saveActionType);
    setShowApprovePrompt(true);
  };

  const forcePublishButtonAction = () => {
    const values = getValues();
    console.log('values@forcePublishButtonAction', saveActionType, values);

    if (new Date(message.scheduledDate) < new Date()) {
      setShowTimeoutPrompt(true);
      return;
    }

    if (values?.relatedCampaign?.isExclusive && !values?.sendToAll) {
      setApproveConfirmAction(ActionType.forcePublish);
      setShowApprovePrompt(true);
      return;
    }
    if (hasSelectedTargetCustomer(values) && !values?.sendToAll) {
      saveButtonAction(false, ActionType.forcePublish);
      return;
    }
    setApproveConfirmAction(ActionType.forcePublish);
    setShowApprovePrompt(true);
  };

  const saveButtonAction = (isSaveOnly, buttonAction = null) => {
    if (new Date(message.scheduledDate) < new Date()) {
      setShowTimeoutPrompt(true);
      return;
    }
    setActionType(buttonAction);
    if (
      buttonAction === ActionType.withdraw ||
      buttonAction === ActionType.unpublish
    ) {
      dispatch(
        createAction('createMessage/updateApprovalStatus')({
          data: getValues(),
          languages: languages,
          actionType: buttonAction,
          afterActions: () => {
            setIsTestSend(false);
            setShowSentPrompt(true);
          },
        }),
      );
    } else {
      dispatch(
        createAction('createMessage/createOrUpdateMessage')({
          values: getValues(),
          actionType: buttonAction,
          afterActions: () => {
            setIsTestSend(false);
            setShowSentPrompt(true);
          },
        }),
      );
    }
  };

  const getTempText = () => {
    switch (approvalStatus) {
      case ApprovalStatus.draft:
        tempActionType = ActionType.update;
        return 'Update';
      case ApprovalStatus.pending:
        if (hasPermission(PermissionCodes.editPendingMessage)) {
          tempActionType = ActionType.update;
          return 'Update';
        }
        tempActionType = ActionType.withdraw;
        return 'Withdraw';
      case ApprovalStatus.published:
        tempActionType = ActionType.tempForPulished;
        return 'Update';
      default:
        tempActionType = ActionType.save;
        return 'Save as a draft';
    }
  };

  const getApprovePromptText = () => {
    console.log('getApprovePromptText:', message);
    if (message?.sendToAll) {
      return 'This message will be sent to all customers';
    }
    if (
      message?.relatedCampaign?.isExclusive &&
      !message?.recipientsFollowCampaign
    ) {
      if (!hasSelectedTargetCustomer(message)) {
        return 'The message will be delivered to customers targeted in the campaign';
      }
      return 'The message will be delivered to customers targeted in both the campaign & message';
    }
    if (
      message?.relatedCampaign?.isExclusive &&
      message?.recipientsFollowCampaign
    ) {
      return 'The message will be delivered to customers targeted in the campaign';
    }
    return 'This message will be sent to all customers';
  };

  const hasTempPermission = () => {
    switch (approvalStatus) {
      case ApprovalStatus.draft:
        return hasPermission(PermissionCodes.editDraftMessage);
      case ApprovalStatus.pending:
        return (
          hasPermission(PermissionCodes.editPendingMessage) ||
          hasPermission(PermissionCodes.withdrawPendingMessage)
        );
      case ApprovalStatus.published:
        return hasPermission(PermissionCodes.createTempMessageFromPublish);
      default:
        return hasPermission(PermissionCodes.createDraftMessage);
    }
  };

  const approvalText = () => {
    switch (approvalStatus) {
      case ApprovalStatus.published:
        return 'Unpublish';
      case ApprovalStatus.pending:
        if (hasPermission(PermissionCodes.publishPendingMessage)) {
          return 'Approve';
        }
        return 'Pending for approval';
      case ApprovalStatus.draft:
      default:
        return 'Submit for approval';
    }
  };

  const saveActionType = useMemo(() => {
    switch (approvalStatus) {
      case ApprovalStatus.published:
        return ActionType.unpublish;
      case ApprovalStatus.pending:
        if (hasPermission(PermissionCodes.publishPendingMessage)) {
          return ActionType.approval;
        }
        return ActionType.pendingForApproval;
      default:
        return ActionType.submitForApproval;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approvalStatus]);

  const hasSavePermission = () => {
    switch (approvalStatus) {
      case ApprovalStatus.pending:
        return (
          hasPermission(PermissionCodes.withdrawPendingMessage) ||
          hasPermission(PermissionCodes.publishPendingMessage)
        );
      case ApprovalStatus.published:
        return (
          hasPermission(PermissionCodes.unPublishMessageToPending) ||
          hasPermission(PermissionCodes.unPublishMessageToDraft)
        );
      case ApprovalStatus.draft:
      default:
        return hasPermission(PermissionCodes.pendingDraftMessage);
    }
  };

  const showForceSavePermission = () => {
    if (
      approvalStatus == ApprovalStatus.pending ||
      approvalStatus == ApprovalStatus.published
    ) {
      return false;
    }
    return hasPermission(PermissionCodes.publishPendingMessage);
  };

  const extraPopContent = useMemo(() => {
    const extraPopContentList = [];
    if (
      message?.channels?.includes(MessageChannel.inbox) ||
      message?.channels?.includes(MessageChannel.push)
    ) {
      extraPopContentList.push({
        content: 'Test Channel',
        action: () => {
          setShowTestPrompt(true);
        },
        requires: PermissionCodes.testMessage,
      });
    }
    extraPopContentList.push({
      content: getTempText(),
      action: () => saveButtonAction(true, tempActionType),
    });
    if (approvalStatus === ApprovalStatus.pending) {
      extraPopContentList.push({
        content: 'Reject',
        action: () => {
          setShowRejectPrompt(true);
        },
        requires: PermissionCodes.publishPendingMessage,
      });
    }
    return extraPopContentList;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ApprovalStatus, message?.channels]);

  const extraButtons = useMemo(() => {
    console.log('extraPopContent', approvalStatus, message.channels);
    const buttonList = [];
    if (
      message?.channels?.includes(MessageChannel.inbox) ||
      message?.channels?.includes(MessageChannel.push)
    ) {
      const testChannelButton = (
        <SaveTempButton
          text="Test Channel"
          action={() => {
            setShowTestPrompt(true);
          }}
        />
      );
      if (hideTestChannelTip || tempHideTestPopover) {
        buttonList.push(testChannelButton);
      } else {
        buttonList.push(
          <OverlayTrigger
            key="top"
            placement="top"
            defaultShow={true}
            overlay={
              <Popover
                id={`popover-positioned-top`}
                className="caution-container"
              >
                <Popover.Content className="caution-content">
                  <div className="test-channel-tip-container d-flex flex-column">
                    <div className="d-flex flex-row">
                      <span>{'Try testing before sending your message.'}</span>
                      <Image
                        className="test-channel-tip-close"
                        src={closeIcon}
                        onClick={() => setTempHideTestPopover(true)}
                      />
                    </div>
                    <div className="d-inline-flex flex-row">
                      <span
                        className="underline"
                        onClick={() => {
                          setTempHideTestPopover(true);
                          hideTestChannelTipByUpdateConfig();
                        }}
                      >
                        {'Do not display again'}
                      </span>
                    </div>
                  </div>
                </Popover.Content>
              </Popover>
            }
          >
            {testChannelButton}
          </OverlayTrigger>,
        );
      }
    }
    if (hasTempPermission()) {
      buttonList.push(
        <SaveTempButton
          text={getTempText()}
          action={() => {
            saveButtonAction(true, tempActionType);
          }}
        />,
      );
    }
    if (
      approvalStatus === ApprovalStatus.pending &&
      hasPermission(PermissionCodes.publishPendingMessage)
    ) {
      buttonList.push(
        <SaveTempButton
          text="Reject"
          action={() => {
            setShowRejectPrompt(true);
          }}
        />,
      );
    }
    if (showForceSavePermission() && hasSavePermission()) {
      buttonList.push(
        <SaveTempButton
          text={approvalText()}
          action={() => approvalButtonAction()}
          disabled={saveActionType === ActionType.pendingForApproval}
        />,
      );
    }
    return buttonList;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    approvalStatus,
    message?.channels,
    hideTestChannelTip,
    tempHideTestPopover,
  ]);

  const primaryButton = useMemo(() => {
    if (showForceSavePermission()) {
      return (
        <SaveButton
          text={'Skip approval and Publish'}
          action={() => forcePublishButtonAction()}
        />
      );
    }
    if (hasSavePermission()) {
      return (
        <SaveButton
          text={approvalText()}
          action={() => approvalButtonAction()}
          disabled={saveActionType === ActionType.pendingForApproval}
        />
      );
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approvalStatus]);

  const estimateCreditItem = ({ title, spent }) => {
    return (
      <div className="credit-item-width">
        <label className="card-subtitle">{title}</label>
        <div className="credit-to-be-spent">
          {formatNumberWithCommas(spent || '0.00')}
        </div>
      </div>
    );
  };

  const renderCurrentMessageCredit = () => {
    return (
      <div className="current-msg-credit-card">
        {estimateCreditItem({
          title: 'Total message to-be-sent of this message',
          spent: estimateCredit,
        })}
        <div className="credit-to-be-spent-detail-content">
          {estimateCreditItem({
            title: 'Messages to-be-sent in Whatsapp',
            spent: estimateWhatsappCredit,
          })}
          {estimateCreditItem({
            title: 'Messages to-be-sent in SMS',
            spent: estimateSmsCredit,
          })}
          {estimateCreditItem({
            title: 'Messages to-be-sent in Email',
            spent: estimateEmailCredit,
          })}
        </div>
        <div>
          {
            'The value is an estimated value which is subject to varied at the time the message is sent out.'
          }
        </div>
      </div>
    );
  };

  const sections = [
    <div>
      <MessageCreditCardContent
        realCreditTotalSpent={realCreditTotalSpent}
        whatsappCreditTotalSpent={whatsappCreditTotalSpent}
        smsCreditTotalSpent={smsCreditTotalSpent}
        emailCreditTotalSpent={emailCreditTotalSpent}
      />
      {renderCurrentMessageCredit()}
    </div>,
  ];

  useEffect(() => {
    let targetedSegmentPks = [];
    if (message?.targetType === TARGET_CUSTOMER_TYPE.DYNAMIC_SEGMENT) {
      targetedSegmentPks = message?.targetedDynamicSegments?.map(
        (item) => item?.pk,
      );
    } else if (message?.targetType === TARGET_CUSTOMER_TYPE.STATIC_SEGMENT) {
      targetedSegmentPks = message?.targetedStaticSegments?.map(
        (item) => item?.pk,
      );
    }
    dispatch(
      createAction('createMessage/estimateMessageCredit')({
        channels: message?.channels,
        sendToAll: message?.sendToAll,
        segments: targetedSegmentPks,
        recipientsFollowCampaign: message?.recipientsFollowCampaign,
        linkedCampaignId: message?.relatedCampaign?.pk,
      }),
    );
    return () => {
      dispatch(
        createAction('createMessage/updateState')({
          estimateCredit: 0,
        }),
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <ContentSections
        sections={sections}
        languageTabContent={{
          containers: languages?.map((item) => ({
            container: renderTabForLanguage(item.code),
            key: item.code,
            title: item.sourceName,
          })),
        }}
        hidePreview={true}
      />
      <BackAndMultipleButtons
        backAction={() => {
          stepChangeAction(true);
        }}
        multipleButtons={[
          <ListButtonsGroup
            hideExtraButtonWidth={600}
            hideAllButtonWidth={400}
            hideExtraPopPosition="top"
            hideAllPopPosition="topRight"
            extraButtons={extraButtons}
            extraPopContent={extraPopContent}
            primaryButton={primaryButton}
            primaryPopContent={{
              requires: PermissionCodes.addAdministratorGroup,
              action: () => saveButtonAction(false, saveActionType),
              content: 'Create Permission Groups',
            }}
          />,
        ]}
      />
      <TestAccountPrompt
        show={showTestPrompt}
        onHide={() => setShowTestPrompt(false)}
        onConfirm={(selectedAccount) => {
          setShowTestPrompt(false);
          dispatch({
            type: 'createMessage/testMessageSend',
            payload: {
              isEdit: location.pathname.includes('edit'),
              values: getValues(),
              customer: selectedAccount,
              afterActions: () => {
                setIsTestSend(true);
                setShowSentPrompt(true);
              },
            },
          });
        }}
      />

      <RejectPrompt
        show={showRejectPrompt}
        onHide={() => setShowRejectPrompt(false)}
        onConfirm={(comment) => {
          setShowRejectPrompt(false);
          dispatch(
            createAction('createMessage/rejectMessage')({
              id: params.id,
              message: comment,
              afterAction: () => {
                const pathname = location.pathname.split('/')[1];
                history.push({
                  pathname: `/${pathname}`,
                });
              },
            }),
          );
        }}
      />

      <BasePrompt
        show={showTimeoutPrompt}
        closeAction={() => setShowTimeoutPrompt(false)}
        rightButton={{
          text: 'Reschedule',
          action: () => {
            setShowTimeoutPrompt(false);
            dispatch({
              type: 'createMessage/updateState',
              payload: {
                currentStep: 2,
              },
            });
          },
        }}
        leftButton={{
          text: 'Cancel',
          action: () => setShowTimeoutPrompt(false),
        }}
        title={'Schedule timeout'}
        description={
          'Your schedule time is expired, please update delivery time.'
        }
      />

      <BasePrompt
        show={showApprovePrompt}
        closeAction={() => setShowApprovePrompt(false)}
        disableClickBackground
        rightButton={{
          text: 'Confirm',
          action: () => {
            setShowApprovePrompt(false);
            if (approveConfirmAction) {
              saveButtonAction(false, approveConfirmAction);
            }
          },
        }}
        leftButton={{
          text: 'Cancel',
          action: () => setShowApprovePrompt(false),
        }}
        description={`${getApprovePromptText()}. Confirm to ${approvalText()?.toLowerCase()}?`}
      />
    </>
  );
}

export default connect(mapPropsToState)(CreateMessageStepFour);
